import { CELL, GROUND } from '../core/config';
import { key, MATERIALS, type Building, type City, type Facility } from './city';
import { roadSegment } from './roads';

/** Small genuine service buildings with ground-level open garages and usable bays. */
export function prepareFacilities(city: City): City {
  if (city.facilities) return city;
  city.facilities = [];
  const all = () => [...city.buildings, ...(city.props ?? []), ...(city.bridges ?? [])];
  let serial = Math.max(...all().map((b) => b.id), ...(city.infrastructure ?? []).map((b) => b.id)) + 1;
  const graph = city.roadGraph!;
  const specifications: { kind: Facility['kind']; x: number; z: number; name?: string; suffix?: string }[] = [
    { kind: 'fire', x: -110, z: -105 },
    { kind: 'police', x: -30, z: 75 },
    { kind: 'hospital', x: 5, z: 120 },
    { kind: 'depot', x: -140, z: 125 },
  ];
  if (city.regional) {
    // The regional city owns these crews and vehicles even before an incident.
    // Separate real two-bay stations share existing roads, with no stacked fleet.
    const stations: [Facility['kind'], number, number, string][] = [
      ['fire', -270, -200, 'Westmere Fire Station'],
      ['fire', 260, -180, 'Eastbank Fire Station'],
      ['fire', -255, 155, 'Orchard Fire Station'],
      ['fire', 250, 245, 'Linden Coast Fire Station'],
      ['fire', -140, -300, 'Northgate Fire Station'],
      ['hospital', -300, -125, 'Westmere Emergency Clinic'],
      ['hospital', 210, -215, 'Northbank Emergency Clinic'],
      ['hospital', -260, 245, 'Orchard Emergency Clinic'],
      ['hospital', 300, 170, 'Eastbank Emergency Clinic'],
      ['hospital', -65, -295, 'Northgate Emergency Clinic'],
      ['police', -245, -65, 'Westmere Police Station'],
      ['police', 220, 10, 'Eastbank Police Station'],
      ['police', -240, 300, 'Foundry Police Station'],
      ['police', 220, 335, 'Coast Police Station'],
      ['depot', -330, 95, 'Orchard Recovery Depot'],
      ['depot', 315, -110, 'Eastbank Recovery Depot'],
    ];
    for (const [i, [kind, x, z, name]] of stations.entries())
      specifications.push({ kind, x, z, name, suffix: `:regional-${i}` });
  }
  for (const spec of specifications) {
    const { kind } = spec;
    const edges = graph.edges
      .filter(
        (e) =>
          e.bridgeId === undefined &&
          Math.abs(graph.nodes[e.from].z - graph.nodes[e.to].z) < 0.1 &&
          Math.abs(graph.nodes[e.from].x - graph.nodes[e.to].x) > 25,
      )
      .sort((a, b) => {
        const distance = (e: typeof a) =>
          Math.hypot(
            (graph.nodes[e.from].x + graph.nodes[e.to].x) / 2 - spec.x,
            graph.nodes[e.from].z - spec.z,
          );
        return distance(a) - distance(b);
      });
    let made = false;
    for (const edge of edges) {
      const a = graph.nodes[edge.from],
        c = graph.nodes[edge.to];
      const x = (a.x + c.x) / 2 - 6.25,
        z = a.z + edge.width / 2 + 4,
        w = 10,
        d = 11;
      if (Math.abs(x) + w * CELL > city.half - 5 || z + d * CELL > city.half - 5) continue;
      if (
        all().some(
          (b) =>
            x - 1 < b.x + b.w * CELL &&
            x + w * CELL + 1 > b.x &&
            z - 3 < b.z + b.d * CELL &&
            z + d * CELL + 1 > b.z,
        )
      )
        continue;
      const id = serial++,
        color =
          kind === 'fire'
            ? 0xba7460
            : kind === 'hospital'
              ? 0xcbd7ce
              : kind === 'police'
                ? 0x8aabb7
                : 0xc4b69a;
      const b: Building = {
        service: kind,
        id,
        x,
        z,
        w,
        d,
        h: 5,
        kind: 'civic',
        category: 'building',
        color,
        accent: kind === 'hospital' ? 0x549b91 : 0x566d75,
        name:
          spec.name ??
          {
            fire: 'Juniper Fire Station',
            police: 'Civic Police Station',
            hospital: 'Riverside Hospital',
            depot: 'Municipal Service Depot',
          }[kind],
        cells: [],
      };
      for (let y = 0; y < 5; y++)
        for (let zz = 0; zz < d; zz++)
          for (let xx = 0; xx < w; xx++) {
            const edgeWall = xx === 0 || xx === w - 1 || zz === 0 || zz === d - 1,
              garage = (zz === 0 || (kind === 'depot' && zz === d - 1)) && xx >= 2 && xx <= 7 && y < 4;
            if (y !== 0 && y !== 4 && !edgeWall) continue;
            if (garage && y > 0) continue;
            const floor = y === 0 && (!edgeWall || garage);
            b.cells.push({
              id: id * 1e6 + key(xx, y, zz),
              x: xx,
              y,
              z: zz,
              hp: MATERIALS.concrete.strength,
              material: 'concrete',
              role: y === 0 || y === 4 ? 'slab' : xx === 0 || xx === w - 1 ? 'column' : 'wall',
              root: y === 0,
              color: y === 0 ? 0xaab5ad : y === 4 ? 0xe0e1d6 : color,
              ...(floor ? { bounds: { x: 0, y: 0, z: 0, w: 1, h: 0.2 / CELL, d: 1 } } : {}),
            });
          }
      city.buildings.push(b);
      const entry = { x: x + (w * CELL) / 2, y: GROUND + 0.2, z: a.z },
        bays = [
          { x: x + 3.25 * CELL, y: GROUND + 0.2, z: z + 3.1 },
          { x: x + 6.75 * CELL, y: GROUND + 0.2, z: z + 3.1 },
        ];
      const driveway = roadSegment(
        serial++,
        { ...edge, width: 7.5 },
        { x: entry.x, y: entry.y, z: entry.z },
        { x: entry.x, y: entry.y, z: z + 1 },
        4,
      );
      driveway.name = `${b.name} access`;
      city.infrastructure!.push(driveway);
      const beds: Facility['beds'] = [];
      if (kind === 'hospital')
        for (let i = 0; i < 4; i++) {
          const xx = 2 + i * 2,
            zz = 7,
            supportCell = id * 1e6 + key(xx, 0, zz),
            bedId = serial++;
          const item: Building = {
            id: bedId,
            name: `Hospital bed ${i + 1}`,
            kind: 'furniture',
            furniture: 'bed',
            category: 'prop',
            x: x + xx * CELL,
            z: z + zz * CELL,
            baseY: GROUND + 0.2,
            w: 1,
            d: 1,
            h: 1,
            color: 0xc9ded4,
            accent: 0x6c9693,
            parentCell: supportCell,
            cells: [
              {
                id: bedId * 1e6,
                x: 0,
                y: 0,
                z: 0,
                hp: 1.4,
                material: 'metal',
                role: 'prop',
                color: 0xc9ded4,
                bounds: { x: 0.1, y: 0, z: 0, w: 0.8, h: 0.45, d: 1 },
              },
            ],
          };
          city.props!.push(item);
          beds.push({ x: item.x + 0.5 * CELL, y: GROUND + 0.2, z: item.z - 0.55, supportCell });
        }
      city.facilities.push({
        id: `facility:${kind}${spec.suffix ?? ''}`,
        kind,
        buildingId: id,
        edge: edge.id,
        node: edge.from,
        entry,
        bays,
        beds,
      });
      made = true;
      break;
    }
    if (!made) throw new Error(`No clear accessible parcel for ${kind} facility`);
  }
  if (city.airport) {
    const id = serial++,
      x = 730,
      z = -299.5;
    const b: Building = {
      id,
      name: 'Alder Airport Fire Annexe',
      service: 'fire',
      kind: 'civic',
      category: 'building',
      district: 'airport',
      x,
      z,
      w: 10,
      d: 11,
      h: 5,
      color: 0xb46f59,
      accent: 0x657e83,
      cells: [],
    };
    for (let y = 0; y < 5; y++)
      for (let zz = 0; zz < 11; zz++)
        for (let xx = 0; xx < 10; xx++) {
          const wall = xx === 0 || xx === 9 || zz === 0 || zz === 10,
            door = zz === 0 && xx >= 2 && xx <= 7;
          if (y !== 0 && y !== 4 && !wall) continue;
          if (door && y > 0 && y < 4) continue;
          b.cells.push({
            id: id * 1e6 + key(xx, y, zz),
            x: xx,
            y,
            z: zz,
            material: 'concrete',
            role: y === 0 || y === 4 ? 'slab' : 'wall',
            root: y === 0,
            hp: MATERIALS.concrete.strength,
            color: y === 4 ? 0xd9dccd : b.color,
            ...(y === 0 && (!wall || door)
              ? { bounds: { x: 0, y: 0, z: 0, w: 1, h: 0.2 / CELL, d: 1 } }
              : {}),
          });
        }
    city.buildings.push(b);
    const entry = { x: x + 6.25, y: GROUND + 0.2, z: -310 },
      bay = { x: x + 6.25, y: GROUND + 0.2, z: z + 3.1 };
    const edge = graph.edges.find((e) => {
      const a = graph.nodes[e.from],
        b = graph.nodes[e.to];
      return a.z === -310 && b.z === -310 && Math.min(a.x, b.x) <= entry.x && Math.max(a.x, b.x) >= entry.x;
    })!;
    const driveway = roadSegment(
      serial,
      { ...edge, width: 7.5 },
      entry,
      { x: entry.x, y: entry.y, z: z + 1 },
      4,
    );
    driveway.name = 'Alder fire annexe access';
    city.infrastructure!.push(driveway);
    city.facilities.push({
      id: 'facility:airport-fire',
      kind: 'fire',
      buildingId: id,
      edge: edge.id,
      node: edge.from,
      entry,
      bays: [bay],
      beds: [],
    });
  }
  return city;
}
